import React from 'react';
import moment from 'moment-timezone';
import { Route, withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import { formatPimDate, formatPostDate } from '@mummssoftware/utils/formatters';
import { isSmallTouchDevice } from '@mummssoftware/utils/web';
import {
  ActiveMedications,
  STRING_TRUE,
  MAINT_STATUS_COMPLETE,
  SYNC_MEDS_TIME_INTERVAL,
  SYNC_MEDS_INTERVAL_MINUTES,
  success as apiAlertSuccess,
  error as apiAlertError,
} from '@mummssoftware/utils/constants';
import {
  getHeaders,
  isBinInMedOrdersBinList,
} from '@mummssoftware/utils/utils';
import { ClearScripts, Pim, Observation, Printing } from '@mummssoftware/utils';
import {
  getActiveMedications,
  getAllMedications,
} from '@mummssoftware/utils/medications/utils';
import { WidgetFallback, Loader, APIAlert } from '@mummssoftware/common-ui';
import {
  SET_MEDS,
  withGlobalContext,
} from '@mummssoftware/global-context/GlobalContext';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Nurse } from '@mummssoftware/utils/roleData';
import {
  withPNGlobalContext,
  REFRESH_PN_DATA,
} from '@mummssoftware/global-context';
import {
  ADD,
  REMOVE,
  ERROR,
  ERR_MSG,
  ONLY_NEED_APPROVAL,
  COMPLETE,
  MEDS_ORDER_TYPE_ADMISSION,
  MEDS_ORDER_TYPE_VERBAL,
} from './constants';
import defaults from '../../translations/en.json';
import { messages } from '../utils/messages';
import { MedProvider } from './MedContext';
import { AppContext } from './AppContext';
import { getMedDoctors } from '../utils/pim';
import PrintLayout from '../Sections/Print/PrintLayout';
import Listing from '../Listing';

const translations = defaults;
/**
 * @typedef AppProps
 * @prop {mumms.User} [currentUser]
 * @prop {mumms.patient} [patient]
 *
 */
const appRootHeights = {
  height: (props) => (props.embedded ? 'initial' : '100vh'),
};
const AppRoot = withStyles((theme) => ({
  appRoot: {
    ...(theme.props.isSafari && theme.props.isMobile ? {} : appRootHeights),
    marginLeft: 'var(--safe-area-inset-left)',
    marginRight: 'var(--safe-area-inset-right)',
    '@media print': {
      overflow: 'visible',
      visibility: 'hidden',
    },
  },
}))(({ children, classes: { appRoot } }) => (
  <div className={appRoot}>{children}</div>
));

const MedsPrintDiv = styled.div`
  @media print {
    page-break-after: always;
  }
`;

const PageBreak = styled.div`
  @media print {
    visibility: hidden;
  }
`;

/**
 * @extends {React.PureComponent<AppProps>}
 */
class App extends React.PureComponent {
  /** @param {AppProps} props */

  constructor(props) {
    super(props);
    const { agency, online, keycloak, patientNumber } = props;
    if (agency) {
      this.agency = agency;
    }

    if (patientNumber) {
      this.patientNumber = patientNumber;
    }

    if (online) {
      this.pim =
        props.pim ||
        new Pim({
          url: process.env.REACT_APP_PIM_BASEURL,
          keycloak,
          agency: this.agency,
          pgRestUrl: process.env.REACT_APP_PGREST_BASEURL,
        });

      this.observation =
        props.observation ||
        new Observation({
          url: process.env.REACT_APP_OBSERVATION_URL,
          keycloak,
          agency: this.agency,
        });
    }

    this.state = {
      patientNumber: this.patientNumber,
      from: props.from,
      to: props.to,
      medications: props.medications || [],
      siteList: [],
      loaded: !online,
      init: !online,
      patient: props.patient || null,
      agency: this.agency,
      siteId: props.siteId,
      maintPayload: {},
      medsOrderType: '',
      printingService: null,
      showAlertMessage: false,
      alertSeverity: 'success',
      disableUpdateMedsBtn: false,
    };
  }

  /**
   * We keep a record of the maint payload with the order information around so that when
   * the user closes the rcopia session, we do not need to re-create the payload, we can
   * just update the status of the one for this session from pending to complete and send
   * that to clearscripts server.
   */
  updateMaintPayload = (newPayload) =>
    this.setState({ maintPayload: newPayload });

  getInitialData = async () => {
    const startLoadingTime = Date.now();

    console.log('** CLEARSCRIPTS WIDGET START **', new Date());

    const { patientNumber, siteId } = this.state;
    const {
      currentUser: externalUser,
      keycloak,
      agency,
      patient: externalPatient,
      isDoctor,
    } = this.props;

    const [currentUser, patient] = await Promise.all([
      externalUser
        ? Promise.resolve(externalUser)
        : this.pim.getUserInfo(this.agency),
      externalPatient
        ? Promise.resolve(externalPatient)
        : this.pim.getPatientHeader(patientNumber, this.agency),
    ]);
    //! IMPORTANT: Pass patient's siteId not current user's
    // fallback to default siteId
    const [attendingAndHospicePhysicianList, allDoctors] = await getMedDoctors(
      this.pim,
      patient.siteId || '17',
      keycloak,
      patient,
      this.agency,
    );

    // Set the siteNickname to the nickname for the user's default site.
    let siteNickname = currentUser.siteNickName;

    // Search the user's site list for the siteId passed in to the dashboard
    if (Array.isArray(currentUser.siteList)) {
      // Since we are looking for an id value, the result of the filter function should be
      // an array with a single member.
      /* eslint-disable */
      // This uses == because it may not be strict equality; === fails here.
      const site = currentUser.siteList.filter((item) => item.id == siteId)[0];
      /* eslint-enable */
      // If we find a site from the filter, then set the siteNickname property. Otherwise,
      // we'll have to use the default that we set above.
      if (site) siteNickname = site.nickName;
    } else {
      // This shouldn't happen; the currentUser object (or the return from PIM's getUserInfo
      // function) should return an object with a siteList property containing an array
      // all of the sites the user has access to.
      console.log(
        `ERROR: ClearScripts::App::getInitialData: currentUser.siteList is not an array: ${currentUser?.siteList}`,
      );
    }

    this.clearScripts = new ClearScripts({
      url: process.env.REACT_APP_CLEARSCRIPTS_BASEURL,
      keycloak,
      agency: this.agency || currentUser.currentHospiceBin,
    });

    this.printingService = new Printing({
      url: window.env.hosts.printingService,
      keycloak,
      agency,
      currentUser,
    });
    // This call expects (requires) the site nickname.
    const rcopiaConfig = await this.clearScripts.getRcopia(siteNickname);

    this.setState({
      patient,
      agency: this.agency,
      doctors: allDoctors,
      hospicePhys: attendingAndHospicePhysicianList,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      userId: currentUser.userId,
      userCredentials: currentUser.userCredentials,
      isRoleGroupsWithEditCred: currentUser.isRoleGroupsWithEditCred,
      siteId: currentUser.siteId,
      hospiceBin: currentUser.currentHospiceBin,
      hospiceName: currentUser.hospiceName,
      siteName: currentUser.siteName,
      siteNickname: currentUser.siteNickname,
      siteList: currentUser.siteList,
      isDoctor: currentUser.isDoctor || isDoctor,
      isNurse: currentUser.userRoleGroups.includes(Nurse) || false,
      isMumms: currentUser.isMumms,
      hbcFirm: currentUser.hbcFirm,
      siteTimezone: currentUser.siteTimezone,
      rcopiaConfig,
      personNumber: currentUser.personNumber,
      clearScriptsLogin: currentUser.clearScriptsLogin,
      showCheckmarks: false,
      approvedMedsHash: {},
      areMedsApproved: false,
      stateCounter: 0,
      startLoadingTime,
      printingService: this.printingService,
    });
  };

  getUserInfoFromPIMPersons = async (preparerusername) => {
    if (!preparerusername) {
      return {
        status: ERROR,
        message: ERR_MSG,
      };
    }

    const { keycloak, agency } = this.props;

    let authProxyUrl = `${process.env.REACT_APP_PGREST_BASEURL}/api/query/person?agency=${agency}`;
    authProxyUrl += `&select=personnumber,firstname,lastname,credentials`;
    authProxyUrl += `&clearscriptslogin=eq.${preparerusername}`;

    const headers = await getHeaders(keycloak);
    headers.set('Authorization', `Bearer ${keycloak.token}`);

    return fetch(authProxyUrl, {
      headers,
      credentials: 'same-origin',
    });
  };

  handleApproveMedsBtnClick = () => {
    this.setState((prevState) => ({
      showCheckmarks: !prevState.showCheckmarks,
    }));
  };

  updateApprovedMedsHash = (med, action) => {
    this.setState((prevState) => {
      const approvedMedsHashRef = prevState.approvedMedsHash;
      if (action === ADD) {
        approvedMedsHashRef[med.id] = med;
      } else if (action === REMOVE) {
        approvedMedsHashRef[med.id] = null;
      }
      return {
        approvedMedsHash: approvedMedsHashRef,
        stateCounter: prevState.stateCounter + 1,
      };
    });
  };

  cancelApprovalProcess = () => {
    this.setState({ approvedMedsHash: {} });
    this.setState({ showCheckmarks: false });

    this.props.dispatch({
      type: ONLY_NEED_APPROVAL,
      payload: false,
    });

    this.updateMedications();
  };

  updateAreMedsApproved = () => {
    this.setState((prevState) => {
      const isUpdated = Object.entries(prevState.approvedMedsHash).some(
        (med) => med[1] !== null,
      );
      return { areMedsApproved: isUpdated };
    });
  };

  handleApproveClick = (date, isDoctor) => {
    let postDate;
    if (!isDoctor && date) {
      postDate = formatPimDate(date || new Date());
    } else {
      postDate = formatPimDate(new Date());
    }

    const approvedMeds = [];

    if (this.state.approvedMedsHash) {
      Object.entries(this.state.approvedMedsHash).forEach((medication) => {
        if (medication[1] !== null) {
          const medToMod = { ...medication[1] };

          medToMod.reconcileddate = postDate;
          medToMod.reconcileduserid = this.state.userId;
          medToMod.reconcileduserfirstname = this.state.firstName;
          medToMod.reconcileduserlastname = this.state.lastName;
          medToMod.reconciledsource = translations.clearscriptsClient;
          medToMod.reconciledphysicianid = medication.orderingphysicianid;
          medToMod.reconciledphysicianfirstname =
            medToMod.orderingphysicianfirstname;
          medToMod.reconciledphysicianlastname =
            medToMod.orderingphysicianlastname;
          medToMod.reconciledphysicianid = medToMod.orderingphysicianid;

          approvedMeds.push(medToMod);
        }
      });
      this.handleMedChange(approvedMeds).then(() => {
        this.cancelApprovalProcess();
      });
    }
  };

  loadMedications = async () => {
    const { patientNumber, isDoctor, personNumber } = this.state;
    const { dashboardEmbedded } = this.props;
    // check Global Context first
    let medications =
      this.props.idgData ||
      this.props.medsGlobal ||
      (await this.clearScripts.getMedsAndPrescriptions(patientNumber));

    // dispatch to Global Context
    !this.props.medsGlobal &&
      this.props.dispatch &&
      this.props.dispatch({
        type: SET_MEDS,
        payload: medications,
      });

    // if the user is a doctor and the app is in cert-app, filter by the logged in physician.
    if (isDoctor && !dashboardEmbedded) {
      medications = medications.filter(
        (med) => med.orderingphysicianid === personNumber,
      );
    }
    this.setState({
      medications,
      loaded: true,
    });
  };

  updateMedications = async () => {
    const fetchGeneration = this.medFetchGeneration || 0;
    const { patientNumber, isDoctor, personNumber } = this.state;
    const { dashboardEmbedded } = this.props;
    let medications = await this.clearScripts.getMedsAndPrescriptions(
      patientNumber,
    );

    // A medication save started while this request was in flight. Drop the
    // result so it cannot put the previous covered flag back.
    if (
      fetchGeneration !== (this.medFetchGeneration || 0) ||
      (this.pendingMedWrites || 0) > 0
    ) {
      return;
    }

    // dispatch to Global Context
    this.props.dispatch &&
      this.props.dispatch({
        type: SET_MEDS,
        payload: medications,
      });

    // if the user is a doctor and the app is in cert-app, filter by the logged in physician.
    if (isDoctor && !dashboardEmbedded) {
      medications = medications.filter(
        (med) => med.orderingphysicianid === personNumber,
      );
    }
    this.setState({
      medications,
      loaded: true,
    });
  };

  setMedsOrderType = (medsOrderType) => {
    this.setState({
      medsOrderType,
    });
  };

  getHbcLocation = (siteId, siteList) => {
    const currentSite = siteList.length
      ? siteList.find(({ id }) => id === siteId)
      : null;
    return currentSite ? currentSite.hbcLocation : null;
  };

  handleAgencyChange = (value) => {
    if (typeof value === 'string' && value !== this.state.hospiceBin) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('agency', value);
      const newRelativePathQuery = `${
        window.location.pathname
      }?${searchParams.toString()}`;
      this.props.history.push(newRelativePathQuery);
      /* eslint-disable no-restricted-globals */
      location.reload();
      /* eslint-enable */
    }
  };

  handleSiteChange = (siteId, siteNickname) => {
    this.setState((prevState) => {
      const hbcLocation = this.getHbcLocation(siteId, prevState.siteList);
      return {
        siteId,
        siteNickname,
        hbcLocation,
        enableClinicalLinks: !!(prevState.hbcFirm && hbcLocation),
      };
    });
  };

  getExternalURL = async () => {
    const { patientNumber } = this.state;
    const url = await this.clearScripts.getExternalUrl(patientNumber);
    const response = {};
    response.status = url.status;
    if (url.status === 403) {
      response.message = <FormattedMessage {...messages.notSetup} />;
      return response;
    }
    response.message = <FormattedMessage {...messages.finished} />;
    response.url = url.url;
    response.embeddable = url.embeddable;
    return response;
  };

  handleSyncMeds = async () => {
    const { patientNumber } = this.state;
    this.setState({ loaded: false });
    await this.clearScripts.syncMeds(patientNumber);
    await this.updateMedications();
    this.setState({ loaded: true });
  };

  handleApproveMeds = async () => {
    const { patientNumber } = this.state;
    const source = 'clearscripts-client';
    await this.clearScripts.approveMeds(patientNumber, source);
    await this.updateMedications();
  };

  /**
   * This workflow for updating maint status is being deprecated by the new med orders
   * work.  However, sites that do not yet have the med orders workflow turned on will need
   * to use the original workflow.
   *
   * TODO:  refactor out once med orders workflow has been implemented for all sites.
   * we need to support
   * @param {*} status
   * @returns
   */
  updateMaintStatusV1 = async (status) => {
    const { patientNumber, medsOrderType } = this.state;
    let success;
    if (
      status === COMPLETE &&
      (this.state.medsOrderType === MEDS_ORDER_TYPE_ADMISSION ||
        this.state.medsOrderType === MEDS_ORDER_TYPE_VERBAL)
    ) {
      success = await this.clearScripts.updateMaintStatus(
        patientNumber,
        status,
        medsOrderType,
      );
    } else {
      success = await this.clearScripts.updateMaintStatus(
        patientNumber,
        status,
      );
    }

    return success;
  };

  pullAndUpdateMedications = async () => {
    const { disableUpdateMedsBtn, agency, patientNumber } = this.state;
    if (disableUpdateMedsBtn) return;

    this.setState({ disableUpdateMedsBtn: true });

    const time = moment().subtract(
      SYNC_MEDS_TIME_INTERVAL,
      SYNC_MEDS_INTERVAL_MINUTES,
    );

    const response = await this.clearScripts.updateAllMeds(
      formatPostDate(time),
      agency,
      patientNumber,
    );

    if (response?.ok) {
      await this.updateMedications();
      this.setState({
        showAlertMessage: true,
        alertSeverity: apiAlertSuccess,
        disableUpdateMedsBtn: false,
      });
    } else {
      this.setState({
        showAlertMessage: true,
        alertSeverity: apiAlertError,
        disableUpdateMedsBtn: false,
      });
    }
  };

  /**
   * This is the updated maint status workflow that is being utilized by the med orders
   * work.
   * @param {*} payload
   * @returns
   */
  updateMaintStatusV2 = async (payload) => {
    const { patientNumber } = this.state;
    const { dispatchPN } = this.props;
    let success = false;

    const { agency } = this.state;
    // Only need to update PN if this is a physician
    // and only when they are closing rcopia session.
    const doPNUpdate =
      dispatchPN &&
      payload &&
      payload.status &&
      payload.status === MAINT_STATUS_COMPLETE &&
      payload.iseprescribing &&
      payload.iseprescribing === STRING_TRUE;

    if (payload) {
      try {
        success = await this.clearScripts.updateNotesMaintStatusv3(
          patientNumber,
          payload,
          agency,
        );
        if (doPNUpdate) {
          dispatchPN({ type: REFRESH_PN_DATA });
        }
      } catch (err) {
        console.error(err);
      }
    }

    return success;
  };

  handleMedChange = async (medication, options = {}) => {
    // isDraft from Immer.js
    const newMed = JSON.parse(JSON.stringify(medication));
    const shouldRefresh = !options.skipRefresh;
    if (shouldRefresh) {
      this.pendingMedWrites = (this.pendingMedWrites || 0) + 1;
      this.medFetchGeneration = (this.medFetchGeneration || 0) + 1;
    }
    try {
      if (medication instanceof Array) {
        await this.clearScripts.putMeds(newMed);
      } else {
        await this.clearScripts.putMed(newMed);
      }

      if (options.skipRefresh) {
        // Keep App + medsGlobal in sync without a full getMedsAndPrescriptions
        // so icon toggles stay correct on widget reopen without flicker.
        this.setState(
          (prevState) => ({
            medications: prevState.medications.map((m) =>
              m.id === newMed.id ? newMed : m,
            ),
          }),
          () => {
            this.props.dispatch &&
              this.props.dispatch({
                type: SET_MEDS,
                payload: this.state.medications,
              });
          },
        );
      } else {
        this.medRefreshQueued = true;
      }
    } finally {
      if (shouldRefresh) {
        this.pendingMedWrites -= 1;
        if (this.pendingMedWrites === 0 && this.medRefreshQueued) {
          this.medRefreshQueued = false;
          this.updateMedications();
        }
      }
    }
  };

  updateLocalMedsFromGlobalMeds = (medsGlobalNew) => {
    this.setState({
      medications: medsGlobalNew,
      loaded: true,
    });
  };

  handleAlertClose = () => this.setState({ showAlertMessage: false });

  componentDidMount = async () => {
    if (this.props.online) {
      await this.getInitialData();
      await this.loadMedications();
      if (!this.state.init) {
        this.setState({ init: true });
      }
      this.props.onWidgetReady && this.props.onWidgetReady('ClearScripts');
      const loadingDuration = (Date.now() - this.state.startLoadingTime) / 1000;

      console.log('## CLEARSCRIPTS WIDGET END ##', loadingDuration, 's');
    }
  };

  componentDidUpdate = (prevProps, prevState) => {
    const { medsGlobal: prevMedsGlobal } = prevProps;
    const { medsGlobal: currMedsGlobal } = this.props;

    if (prevState.stateCounter !== this.state.stateCounter) {
      this.updateAreMedsApproved();
    }

    // Clearscripts uses a local copy of medications that can be filtered to display.
    // Whenever the Global Meds list updates, listen for that change.  When there is
    // a change to global meds, update the local med list with a fresh copy of the
    // global meds list, so that it can be processed and filtered for display.
    const updateLocalMeds =
      prevMedsGlobal &&
      prevMedsGlobal.length &&
      currMedsGlobal &&
      currMedsGlobal.length &&
      currMedsGlobal.length !== prevMedsGlobal.length;

    if (updateLocalMeds) {
      this.updateLocalMedsFromGlobalMeds(currMedsGlobal);
    }
  };

  render() {
    const {
      patient,
      isDoctor,
      firstName,
      lastName,
      clearScriptsLogin,
      hbcLocation,
      hospiceBin,
      hospiceName,
      isMumms,
      siteId,
      siteList,
      siteName,
      siteNickname,
      from,
      medications,
      init,
      loaded,
      to,
      siteTimezone,
      agency,
      rcopiaConfig,
      hospicePhys,
      maintPayload,
      showAlertMessage,
      alertSeverity,
      disableUpdateMedsBtn,
    } = this.state;
    const {
      embedded,
      logout,
      toSearch,
      maintainMed,
      online,
      dashboardEmbedded,
      appTitle,
      onClick,
      onRemove,
      doNotShow,
      patientTopBar,
      hideActionBar,
      smallWidget,
      watermark,
      viewMode,
      printConfig,
      onWidgetReady,
      currentUser,
    } = this.props;

    const readonly =
      this.props.readonly || !this.state.isRoleGroupsWithEditCred;

    const today = new Date();
    // All Meds show active meds first, discharged meds last
    const printMeds =
      printConfig && printConfig.view === ActiveMedications
        ? getActiveMedications(medications, today)
        : getAllMedications(medications, today);

    const widgetTitle =
      printConfig && printConfig.view === ActiveMedications
        ? defaults.printTitleActiveMeds
        : defaults.printTitleAllMeds;

    // isPrintMode is a read only view from dashboard, this is history route in stand alone mode
    const isPrintMode = viewMode === 'print';

    const RootLoader = <Loader key="root-loader" />;

    const isMobile = isSmallTouchDevice();

    const isInMedOrdersBinList = isBinInMedOrdersBinList(hospiceBin);

    const appContext = {
      userId: this.state.userId,
      userCredentials: this.state.userCredentials,
      isDoctor: this.state.isDoctor,
      isNurse: this.state.isNurse,
      firstName,
      lastName,
      hospiceBin,
      hospiceName,
      isMobile,
      isMumms,
      logout,
      siteName,
      patient,
      hideActionBar,
      readonly,
      smallWidget,
      dashboardEmbedded,
      watermark,
      viewMode,
      siteTimezone,
      rcopiaConfig,
      approvedMedsHash: this.state.approvedMedsHash,
      setMedsOrderType: this.setMedsOrderType,
      handleApproveMedsBtnClick: this.handleApproveMedsBtnClick,
      getUserInfoFromPIMPersons: this.getUserInfoFromPIMPersons,
      // new additions from med orders code.
      updateMaintStatus: this.updateMaintStatus,
      updateMaintPayload: this.updateMaintPayload,
      isInMedOrdersBinList,
      updateMaintStatusV1: this.updateMaintStatusV1,
      updateMaintStatusV2: this.updateMaintStatusV2,
      clearScriptsLogin,
      allDoctors: this.state.doctors,
      attendingAndHospicePhysicianList: hospicePhys,
      currentUser,
      printingService: this.state.printingService,
      observation: this.observation,
      agency: this.agency,
      pullAndUpdateMedications: this.pullAndUpdateMedications,
      disableUpdateMedsBtn,
    };

    if (!init) {
      return (
        <WidgetFallback
          embedded={embedded}
          appTitle={translations.widgetTitle}
          smallWidget={smallWidget}
        />
      );
    }

    return (
      <MedProvider
        clearScripts={this.clearScripts}
        handleMedChange={this.handleMedChange}
        from={from}
        to={to}
        toSearch={toSearch}
        medications={medications}
      >
        <AppContext.Provider value={appContext}>
          {/* isPrintMode  */}
          {isPrintMode && (
            <React.Fragment>
              <MedsPrintDiv id="meds-print-div">
                <PrintLayout
                  agency={agency}
                  medications={printMeds}
                  onWidgetReady={onWidgetReady}
                  patient={patient}
                  facility={patient.facility}
                  hospiceName={patient.hospiceName}
                  widgetTitle={widgetTitle}
                  isPrintMode={isPrintMode}
                  printConfig={printConfig}
                  observation={this.observation}
                />
              </MedsPrintDiv>
              <PageBreak />
            </React.Fragment>
          )}

          <AppRoot embedded={embedded}>
            {!loaded ? RootLoader : null}
            {showAlertMessage ? (
              <APIAlert
                errorInfo={{
                  text:
                    alertSeverity === apiAlertSuccess ? (
                      <FormattedMessage {...messages.updateMedsAlertSuccess} />
                    ) : (
                      <FormattedMessage {...messages.updateMedsAlertError} />
                    ),
                  severity: alertSeverity,
                }}
                handleClose={this.handleAlertClose}
                persistentError
              />
            ) : null}
            {/* if no printMode then full or small or standalone modes  */}
            {!isPrintMode && (
              <Route
                path={['/']}
                render={(props) => (
                  <Listing
                    {...props}
                    from={from}
                    to={to}
                    toSearch={toSearch}
                    getExternalURL={this.getExternalURL}
                    handleSyncMeds={this.handleSyncMeds}
                    handleApproveMeds={this.handleApproveMeds}
                    handleMedChange={this.handleMedChange}
                    updateApprovedMedsHash={this.updateApprovedMedsHash}
                    showCheckmarks={this.state.showCheckmarks}
                    cancelApprovalProcess={this.cancelApprovalProcess}
                    areMedsApproved={this.state.areMedsApproved}
                    handleApproveClick={this.handleApproveClick}
                    embedded={embedded}
                    maintainMed={maintainMed}
                    patientTopBar={patientTopBar}
                    readonly={readonly}
                    online={online}
                    dashboardEmbedded={dashboardEmbedded}
                    appTitle={appTitle}
                    onClick={onClick}
                    onRemove={onRemove}
                    doNotShow={doNotShow}
                    getInitialData={this.getInitialData}
                    updateMedications={this.updateMedications}
                    updateMaintStatusV1={this.updateMaintStatusV1}
                    updateMaintStatusV2={this.updateMaintStatusV2}
                    patient={this.state.patient}
                    doctors={this.state.doctors}
                    hospicePhys={hospicePhys}
                    handleAgencyChange={this.handleAgencyChange}
                    handleSiteChange={this.handleSiteChange}
                    certContext={AppContext}
                    smallWidget={smallWidget}
                    logout={logout}
                    viewMode={viewMode}
                    maintPayload={maintPayload}
                    isInMedOrdersBinList={isInMedOrdersBinList}
                    currentUser={currentUser}
                    {...{
                      isDoctor,
                      firstName,
                      lastName,
                      siteId,
                      siteNickname,
                      siteList,
                      hospiceBin,
                      hospiceName,
                      hbcLocation,
                      isMobile,
                      isMumms,
                    }}
                  />
                )}
              />
            )}
          </AppRoot>
        </AppContext.Provider>
      </MedProvider>
    );
  }
}

export default withRouter(withGlobalContext(withPNGlobalContext(App)));
